// apps/web/src/lib/compras/fornecedores.ts — Fase 13, Plano 17 (COMP-01, D-18).
// Regras puras da lista de fornecedores: filtros -> query string, paleta de situação e bloqueio da inativação.
import type { FornecedorResumo, SituacaoFornecedor } from '@pieltec/contracts';

export interface FiltrosFornecedores {
  busca: string;
  /** '' = todas. */
  situacao: '' | SituacaoFornecedor;
  incluirInativos: boolean;
}

export function filtrosFornecedoresVazios(): FiltrosFornecedores {
  return { busca: '', situacao: '', incluirInativos: false };
}

export function filtrosFornecedoresAtivos(filtros: FiltrosFornecedores): boolean {
  return filtros.busca.trim() !== '' || filtros.situacao !== '' || filtros.incluirInativos;
}

/** Query string (sem `?`) com só os filtros ativos. Filtrar por "Inativo" implica incluir inativos. */
export function montarQueryFornecedores(filtros: FiltrosFornecedores, pagina: number): string {
  const params = new URLSearchParams();
  params.set('pagina', String(pagina));
  const busca = filtros.busca.trim();
  if (busca !== '') params.set('busca', busca);
  if (filtros.situacao !== '') params.set('situacao', filtros.situacao);
  if (filtros.incluirInativos || filtros.situacao === 'INATIVO') params.set('incluirInativos', 'true');
  return params.toString();
}

export interface ApresentacaoSituacaoFornecedor {
  readonly rotulo: string;
  readonly icone: 'UserCheck' | 'CheckCircle2' | 'XCircle';
  readonly classes: string;
}

/** Ativo emerald, A confirmar amber (UserCheck), Inativo slate; sempre ícone + texto. */
export function apresentacaoSituacaoFornecedor(situacao: SituacaoFornecedor): ApresentacaoSituacaoFornecedor {
  switch (situacao) {
    case 'A_CONFIRMAR':
      return { rotulo: 'A confirmar', icone: 'UserCheck', classes: 'bg-amber-50 text-amber-700 border-amber-200' };
    case 'ATIVO':
      return { rotulo: 'Ativo', icone: 'CheckCircle2', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'INATIVO':
      return { rotulo: 'Inativo', icone: 'XCircle', classes: 'bg-slate-100 text-slate-500 border-slate-200' };
  }
}

export type AcaoFornecedor = 'editar' | 'inativar' | 'reativar';

/** Sem compras:gerenciar nenhuma ação; inativar só quando o fornecedor ainda não está inativo. */
export function acoesDoFornecedor(fornecedor: Pick<FornecedorResumo, 'situacao'>, permissoes: readonly string[]): AcaoFornecedor[] {
  if (!permissoes.includes('compras:gerenciar')) return [];
  return fornecedor.situacao === 'INATIVO' ? ['editar', 'reativar'] : ['editar', 'inativar'];
}

/** Texto de bloqueio da inativação quando há pedido em aberto; null quando pode inativar. */
export function bloqueioInativacao(pedidosEmAberto: number): string | null {
  if (pedidosEmAberto <= 0) return null;
  return `Este fornecedor tem ${pedidosEmAberto} pedido(s) em aberto. Conclua ou cancele antes de inativar.`;
}

/** "Página {N} de {T}" com T no mínimo 1. */
export function totalDePaginas(total: number, tamanhoPagina: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, tamanhoPagina)));
}
